import { fromUnixTime, format, addMinutes } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { minZero } from "./math";
import { DAYS, DAYS_SHORT } from "../constants/date";
import { AM, PM, TIMEZONES, TIMEZONES_DST } from "../constants/time";
import { DELIVERY } from "../constants/order";
import { OrderType, LocationHours } from "../types";

type Timezone = keyof typeof TIMEZONES;

export const formatHour = (hour: string) => hour.trim().substring(0, 5);

export const toCivilianTime = (time: string): string => {
  let [hour, minute]: number[] = time.split(`:`).map((i) => parseInt(i, 10));

  if (hour >= 24) hour %= 24;

  if (minute >= 60) minute %= 60;

  const suffix = hour >= 12 ? PM : AM;

  return `${((hour + 11) % 12) + 1}:${minute
    .toString()
    .padStart(2, `0`)} ${suffix}`;
};

export const toDayOfWeek = (number: number, isShort = false): string =>
  (isShort ? DAYS_SHORT : DAYS)[minZero(minZero(number) % 7)];

export const getFlankDays = (k = []) => {
  if (k.length > 1) {
    return [toDayOfWeek(k[0]), toDayOfWeek(k[k.length - 1])].join(`-`);
  }

  return toDayOfWeek(k[0]);
};

export const sortAndSplitHours = (obj: Record<string, unknown> = {}) => {
  const splitBySequence = (items = [], differences: number[] = []) =>
    items.reduce(
      (memo, item) => {
        const lastArray = memo[memo.length - 1];
        const lastItem = lastArray[lastArray.length - 1];

        if (!lastItem || differences.includes(item - lastItem)) {
          lastArray.push(item);
        } else {
          memo.push([item]);
        }

        return memo;
      },
      [[]],
    );

  const x = Object.entries(obj)
    .reduce((accu: any, i: any) => {
      const daysInSequence = splitBySequence(i[1], [1, -6]);

      return [
        ...accu,
        ...daysInSequence.map((y) => ({
          days: y,
          hours: i[0],
          order: y[0] ? y[0] : 7,
          text: getFlankDays(y),
        })),
      ];
    }, [])
    .sort((a: { order: any }, b: { order: any }) => a.order - b.order);

  return x;
};

export const combineHours = (hours: Array<LocationHours>) => {
  const hoursObject = hours.reduce(
    (accu: Record<OrderType, any>, i: any) => {
      const hoursObj = accu;

      if (!i.deliveryOpen && !i.deliveryClose) {
        hoursObj.delivery.Closed = [...(hoursObj.delivery.Closed || []), i.day];
      } else {
        const deliveryOpen = toCivilianTime(formatHour(i.deliveryOpen));
        const deliveryClose = toCivilianTime(formatHour(i.deliveryClose));

        if (hoursObj.delivery[`${deliveryOpen} - ${deliveryClose}`]) {
          hoursObj.delivery[`${deliveryOpen} - ${deliveryClose}`].push(i.day);
        } else {
          hoursObj.delivery[`${deliveryOpen} - ${deliveryClose}`] = [i.day];
        }
      }

      if (!i.pickupOpen && !i.pickupClose) {
        hoursObj.pickup.Closed = [...(hoursObj.pickup.Closed || []), i.day];
      } else {
        const pickupOpen = toCivilianTime(formatHour(i.pickupOpen));
        const pickupClose = toCivilianTime(formatHour(i.pickupClose));

        if (hoursObj.pickup[`${pickupOpen} - ${pickupClose}`]) {
          hoursObj.pickup[`${pickupOpen} - ${pickupClose}`].push(i.day);
        } else {
          hoursObj.pickup[`${pickupOpen} - ${pickupClose}`] = [i.day];
        }
      }

      if (!i.dineInOpen && !i.dineInClose) {
        hoursObj.kiosk.Closed = [...(hoursObj.kiosk.Closed || []), i.day];
      } else {
        const dineInOpen = toCivilianTime(formatHour(i.dineInOpen));
        const dineInClose = toCivilianTime(formatHour(i.dineInClose));

        if (hoursObj.kiosk[`${dineInOpen} - ${dineInClose}`]) {
          hoursObj.kiosk[`${dineInOpen} - ${dineInClose}`].push(i.day);
        } else {
          hoursObj.kiosk[`${dineInOpen} - ${dineInClose}`] = [i.day];
        }
      }

      return {
        delivery: { ...accu.delivery, ...hoursObj.delivery },
        kiosk: { ...accu.kiosk, ...hoursObj.kiosk },
        pickup: { ...accu.pickup, ...hoursObj.pickup },
      };
    },
    {
      delivery: {},
      kiosk: {},
      pickup: {},
    },
  );

  return {
    delivery: sortAndSplitHours(hoursObject.delivery),
    kiosk: sortAndSplitHours(hoursObject.kiosk),
    pickup: sortAndSplitHours(hoursObject.pickup),
  };
};

export const combineValues = (
  { delivery = 0, prep = 0, pickup = 0 },
  orderType: OrderType,
) => {
  const safePickup = pickup;
  const safeDelivery = delivery;
  const safePrep = prep;

  const baseValue = orderType === DELIVERY ? safeDelivery : safePickup;

  return safePrep + baseValue;
};

export const formatTime = (min: number): string => {
  if (min === 0) {
    return `0 minutes`;
  }

  const hours = min ? Math.floor(min / 60) : 0;
  const minutes = min ? min % 60 : 0;
  const values = [];

  if (hours) {
    values.push(`${hours} hour${hours > 1 ? `s` : ``}`);
  }

  if (minutes) {
    values.push(`${minutes} minute${minutes > 1 ? `s` : ``}`);
  }

  return values.join(` and `);
};

export const stdTimezoneOffset = (): number => {
  const baseDate = new Date();
  const jan = new Date(baseDate.getFullYear(), 0, 1);
  const jul = new Date(baseDate.getFullYear(), 6, 1);

  return Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
};

export const IS_DST_OBSERVED: boolean =
  new Date().getTimezoneOffset() < stdTimezoneOffset();

export const mapTimeZone = (
  timeZone: Timezone,
  isDstObserved: boolean = IS_DST_OBSERVED,
): string => {
  const timeZoneMapping = isDstObserved ? TIMEZONES_DST : TIMEZONES;

  return timeZoneMapping[timeZone] || ``;
};

export const displayScheduledAtTime = (i: number, timeZone: string): string => {
  const timeStart = fromUnixTime(i);
  const zoneTime = utcToZonedTime(timeStart, timeZone);

  return format(zoneTime, `h:mm a`);
};

export const displayScheduledAtTimeDelivery = (
  i: number,
  timeZone: string,
): string => {
  const timeStart = fromUnixTime(i);
  const timeEnd = addMinutes(timeStart, 15);

  return `${format(utcToZonedTime(timeStart, timeZone), `h:mm a`)} - ${format(
    utcToZonedTime(timeEnd, timeZone),
    `h:mm a`,
  )}`;
};

export const hoursToMs = (hours: number): number => hours * 60 * 60 * 1000;

// TODO: Automatically countdown from 24
export const hours = {
  24: hoursToMs(24),
};
